import React, { useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector, useDispatch } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import { IAppState } from 'infra/AppState'
import { SeedPhase } from 'state/seed/seed.types'
import { seedTransactions } from 'state/seed/seed.action'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { NEWDASHBOARD_EVENTS } from 'state/new-summary/events/events.types'
import { THEME } from 'components/theme'

const RED = '#ED4017'
const RED_BG = '#FFE4DD'
const TRACK = `rgba(${THEME.inkRgb}, 0.1)`
const MUTED = `rgba(${THEME.inkRgb}, 0.6)`
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'
const POP = '$syncPop 0.45s cubic-bezier(0.3, 1.6, 0.5, 1)'

const useStyles = createUseStyles({
  '@keyframes syncRipple': {
    from: { transform: 'scale(0.8)', opacity: 0.5 },
    to: { transform: 'scale(1.7)', opacity: 0 }
  },
  '@keyframes syncPop': {
    from: { transform: 'scale(0.5)' },
    to: { transform: 'none' }
  },
  '@keyframes syncFlow': {
    to: { backgroundPosition: '14px 0' }
  },
  '@keyframes syncRide': {
    '0%': { left: 'calc(-50% + 36px)', opacity: 0, transform: 'scale(0.5)' },
    '15%': { opacity: 1, transform: 'none' },
    '85%': { opacity: 1, transform: 'none' },
    '100%': { left: 'calc(50% - 62px)', opacity: 0, transform: 'scale(0.5)' }
  },
  '@keyframes syncSwap': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' }
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: THEME.brandWhite,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100000,
    overflow: 'auto'
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '76px',
    paddingLeft: '16px',
    paddingRight: '16px'
  },
  headerIcon: {
    width: '48px',
    height: '48px'
  },
  title: {
    marginTop: '12px',
    fontSize: '16px',
    fontWeight: 600,
    color: THEME.brandBlack,
    textAlign: 'center'
  },
  journey: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '640px',
    // Optical centre: sits a little above the middle of the page.
    margin: 'auto',
    padding: '24px 16px 14vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px'
  },
  line: {
    display: 'flex',
    width: '100%'
  },
  station: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  // Track from the previous station into this one.
  segment: {
    position: 'absolute',
    top: '37px',
    left: 'calc(-50% + 48px)',
    width: 'calc(100% - 96px)',
    height: '6px',
    borderRadius: '3px',
    backgroundColor: TRACK,
    transition: 'background-color 0.4s'
  },
  segmentActive: {
    backgroundImage: `linear-gradient(90deg, ${THEME.brandGreen} 50%, transparent 50%)`,
    backgroundSize: '14px 100%',
    animation: '$syncFlow 0.45s linear infinite',
    [REDUCED_MOTION]: { animation: 'none' }
  },
  segmentDone: {
    backgroundColor: THEME.brandGreen
  },
  // The khata file (one export per account type) riding the live segment.
  parcel: {
    position: 'absolute',
    zIndex: 2,
    top: '24px',
    left: 'calc(-50% + 36px)',
    boxSizing: 'border-box',
    width: '26px',
    height: '32px',
    borderRadius: '6px',
    backgroundColor: THEME.brandWhite,
    border: `1.5px solid ${THEME.brandGreen}`,
    color: THEME.brandGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.18)',
    animation: '$syncRide 1.8s cubic-bezier(0.5, 0, 0.4, 1) infinite',
    [REDUCED_MOTION]: { display: 'none' },
    '& > svg': {
      width: '16px',
      height: '16px'
    }
  },
  icon: {
    position: 'relative',
    zIndex: 1,
    width: '80px',
    height: '80px',
    marginBottom: '12px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: THEME.brandGrey,
    color: MUTED,
    transition: 'background-color 0.4s, color 0.3s',
    '& > svg': {
      width: '36px',
      height: '36px'
    }
  },
  iconActive: {
    backgroundColor: THEME.brandLightGreen,
    color: THEME.brandGreen,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      border: `2px solid ${THEME.brandGreen}`,
      opacity: 0,
      animation: '$syncRipple 2s ease-out infinite'
    },
    '&::after': {
      animationDelay: '1s'
    },
    [REDUCED_MOTION]: {
      '&::before, &::after': { animation: 'none' }
    }
  },
  iconDone: {
    backgroundColor: THEME.brandGreen,
    color: THEME.onBrand,
    animation: POP,
    [REDUCED_MOTION]: { animation: 'none' }
  },
  badge: {
    position: 'absolute',
    right: '-3px',
    bottom: '-3px',
    boxSizing: 'border-box',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: THEME.onBrand,
    border: `2.5px solid ${THEME.brandWhite}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: `${POP} 0.15s both`,
    [REDUCED_MOTION]: { animation: 'none' },
    '& > svg': {
      width: '13px',
      height: '13px'
    }
  },
  stationName: {
    fontSize: '15px',
    fontWeight: 600,
    color: THEME.brandBlack
  },
  stationWord: {
    marginTop: '2px',
    minHeight: '20px',
    fontSize: '13px',
    color: MUTED
  },
  stationWordActive: {
    fontWeight: 600,
    color: THEME.brandGreen
  },
  sentence: {
    maxWidth: '560px',
    minHeight: '60px',
    fontSize: '22px',
    lineHeight: '30px',
    fontWeight: 600,
    color: THEME.brandBlack,
    textAlign: 'center',
    animation: '$syncSwap 0.45s ease both',
    [REDUCED_MOTION]: { animation: 'none' }
  },
  count: {
    color: THEME.brandGreen,
    fontVariantNumeric: 'tabular-nums'
  },
  assure: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '8px 20px',
    fontSize: '13px',
    color: MUTED
  },
  assureItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    '& > svg': {
      flexShrink: 0,
      width: '16px',
      height: '16px'
    }
  },
  errorCard: {
    backgroundColor: RED_BG,
    borderRadius: '16px',
    margin: '20px 16px 8px 16px',
    padding: '16px',
    fontSize: '14px',
    color: THEME.brandBlack
  },
  spacer: {
    flex: 1
  },
  retryButton: {
    margin: '8px 16px 24px 16px',
    padding: '12px 20px',
    border: 'none',
    borderRadius: '50px',
    backgroundColor: THEME.brandGreen,
    color: THEME.onBrand,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  }
})

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" fill={RED} d="M1 21H23L12 2Zm12 -3H11V16h2Zm0 -4H11V10h2Z" />
  </svg>
)

const LineIcon: React.FC<{ d: string; stroke?: string; strokeWidth?: number }> = ({
  d,
  stroke = 'currentColor',
  strokeWidth = 1.8
}) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CLOUD_PATH = 'M7 18a4 4 0 0 1-.5-7.97A5.5 5.5 0 0 1 17.2 9.1 4.5 4.5 0 0 1 17 18z'
const COMPUTER_PATH = 'M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V15H4zM2.5 18.5h19'
const LEDGER_PATH =
  'M5 19.5v-15A2.5 2.5 0 0 1 7.5 2H19v15H7.5A2.5 2.5 0 0 0 5 19.5 2.5 2.5 0 0 0 7.5 22H19M9 7h6M9 11h4'
const CHECK_PATH = 'M6 12.5l4 4 8-9'
const FILE_PATH = 'M6 3h8l5 5v13H6zM14 3v5h5M9 13h6M9 17h6'
const TAB_PATH = 'M3 5h18v12H3zM8 21h8M12 17v4'
const ONCE_PATH = 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM8 12.5l2.8 2.8L16 9.5'

type StationState = 'done' | 'active' | 'pending'

const PHASE_ORDER: SeedPhase[] = ['started', 'downloading', 'preparing']

// One station per seed phase; the data "travels" left to right between them.
const STATIONS: Array<{ name: string; icon: string; words: Record<StationState, string> }> = [
  {
    name: 'OkCredit cloud',
    icon: CLOUD_PATH,
    words: { pending: '', active: 'Finding your khata…', done: 'Found' }
  },
  {
    name: 'This computer',
    icon: COMPUTER_PATH,
    words: { pending: 'Up next', active: 'Receiving…', done: 'Received' }
  },
  {
    name: 'Your ledger',
    icon: LEDGER_PATH,
    // Never 'done' on screen: the dashboard replaces this screen on success.
    words: { pending: 'Up next', active: 'Arranging…', done: '' }
  }
]

const COUNT_UP_MS = 1200

/** Eases the shown number up to `target` whenever the target grows. */
const useCountUp = (target: number): number => {
  const [shown, setShown] = useState(0)
  const shownRef = useRef(0)

  useEffect(() => {
    const show = (value: number) => {
      shownRef.current = value
      setShown(value)
    }
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || target <= 0) {
      show(target)
      return undefined
    }
    let frame = 0
    const from = Math.min(shownRef.current, target)
    const startedAt = performance.now()
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / COUNT_UP_MS)
      const eased = 1 - Math.pow(1 - progress, 3)
      show(Math.round(from + (target - from) * eased))
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      }
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target])

  return shown
}

interface Props {
  businessId: string
}

/**
 * Blocking post-login sync screen. While seeding, the three seed phases are shown
 * as a journey (OkCredit cloud → This computer → Your ledger) with a plain-language
 * sentence underneath; Android's KMP `SyncScreen` shows the same phases as a
 * stepper. On failure: "Sync Failed" header + error card + a Retry button (no
 * skip/cancel — matches Android). Only the ACTIVE business renders this;
 * background businesses seed silently.
 */
const SyncScreen: React.FC<Props> = ({ businessId }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const seed = useSelector((app: IAppState) => app.Seed.byBusinessId[businessId])
  const failed = seed?.status === 'failed'
  const phase: SeedPhase = seed?.phase || 'started'
  const currentIndex = PHASE_ORDER.indexOf(phase)
  const entryCount = useCountUp(seed?.entryCount || 0)
  const shownAt = useRef(Date.now())

  useEffect(() => {
    dispatch(pushEvent(NEWDASHBOARD_EVENTS.SYNC_SCREEN_VIEWED, { business_id: businessId, phase }))
    // Mount only: the phase at first paint is part of the view event
  }, [])

  useEffect(() => {
    if (phase === 'started') {
      return
    }
    dispatch(
      pushEvent(NEWDASHBOARD_EVENTS.SYNC_SCREEN_PHASE_CHANGED, {
        business_id: businessId,
        phase,
        elapsed_ms: Date.now() - shownAt.current
      })
    )
  }, [businessId, dispatch, phase])

  const retry = () => {
    dispatch(
      pushEvent(NEWDASHBOARD_EVENTS.SYNC_SCREEN_RETRY_CLICKED, {
        business_id: businessId,
        error: seed?.error
      })
    )
    dispatch(seedTransactions({ businessId, isActive: true }))
  }

  if (failed) {
    return (
      <div className={classes.overlay} data-testid="txn-sync-screen">
        <div className={classes.header}>
          <WarningIcon className={classes.headerIcon} />
          <div className={classes.title}>{t('dashboard:Sync Failed')}</div>
        </div>
        <div className={classes.errorCard}>
          {t('dashboard:Something went wrong while syncing your data. Please try again.')}
        </div>
        <div className={classes.spacer} />
        <button className={classes.retryButton} onClick={retry}>
          {t('dashboard:Retry')}
        </button>
      </div>
    )
  }

  return (
    <div className={classes.overlay} data-testid="txn-sync-screen">
      <div className={classes.journey}>
        <div className={classes.line}>
          {STATIONS.map((station, index) => {
            const state = stationState(index, currentIndex)
            return (
              <div className={classes.station} key={station.name}>
                {index > 0 && (
                  <div
                    className={`${classes.segment} ${
                      state === 'active' ? classes.segmentActive : ''
                    } ${state === 'done' ? classes.segmentDone : ''}`}
                  />
                )}
                {index > 0 && state === 'active' && (
                  <div className={classes.parcel} aria-hidden="true">
                    <LineIcon d={FILE_PATH} />
                  </div>
                )}
                <div
                  className={`${classes.icon} ${state === 'active' ? classes.iconActive : ''} ${
                    state === 'done' ? classes.iconDone : ''
                  }`}
                >
                  <LineIcon d={station.icon} />
                  {state === 'done' && (
                    <span className={classes.badge}>
                      <LineIcon d={CHECK_PATH} stroke={THEME.brandGreen} strokeWidth={2.8} />
                    </span>
                  )}
                </div>
                <div className={classes.stationName}>{t(`dashboard:${station.name}`)}</div>
                <div
                  className={`${classes.stationWord} ${
                    state === 'active' ? classes.stationWordActive : ''
                  }`}
                >
                  {station.words[state] && t(`dashboard:${station.words[state]}`)}
                </div>
              </div>
            )
          })}
        </div>
        <div className={classes.sentence} key={phase}>
          {phase === 'started' && t('dashboard:Finding your khata on OkCredit cloud…')}
          {phase === 'downloading' && t('dashboard:Your entries are travelling to this computer…')}
          {phase === 'preparing' &&
            (seed?.entryCount ? (
              <Trans
                i18nKey="dashboard:Entries arrived, arranging your ledger"
                values={{ count: entryCount.toLocaleString('en-IN') }}
                components={{ b: <span className={classes.count} /> }}
              />
            ) : (
              t('dashboard:Arranging your ledger…')
            ))}
        </div>
        <div className={classes.assure}>
          <span className={classes.assureItem}>
            <LineIcon d={TAB_PATH} stroke={THEME.brandGreen} strokeWidth={2} />
            {t('dashboard:Keep this tab open')}
          </span>
          <span className={classes.assureItem}>
            <LineIcon d={ONCE_PATH} stroke={THEME.brandGreen} strokeWidth={2} />
            {t('dashboard:Only needed the first time on this computer')}
          </span>
        </div>
      </div>
    </div>
  )
}

const stationState = (index: number, currentIndex: number): StationState => {
  if (index < currentIndex) {
    return 'done'
  }
  if (index === currentIndex) {
    return 'active'
  }
  return 'pending'
}

export default SyncScreen
